import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { teacherImageList } from '../../constants/teacherImages';
import { useNavigate } from 'react-router';
import { classImageList } from '../../constants/classImages';
import ClassCard from './components/ClassCard';

const ClassList = () => {
  const navigate = useNavigate();
  const API_URL = import.meta.env.VITE_API_URL;
  const [activeTab, setActiveTab] = useState<'this' | 'next'>('this'); //모바일일때 탭으로 이번주 / 다음주 구분노출

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['classListAll'],
      queryFn: async ({ pageParam }) => {
        const res = await fetch(
          `${API_URL}/bbs/ajax.get_class_schedule_list.php?page=${pageParam}`
        );
        return res.json();
      },
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.pagination.page;
        const totalPage = lastPage.data.pagination.total_page;
        return currentPage < totalPage ? currentPage + 1 : undefined;
      },
      initialPageParam: 1
    });

  const allItems = (data?.pages.flatMap((page) => page.data.list) ?? []).map(
    (item) => ({
      ...item,
      file: classImageList[item.name] || null,
      teacherProfiles: item.teachers?.map((name: string) => ({
        name,
        image: teacherImageList[name] || null
      }))
    })
  );

  const thisWeek = allItems.filter((item) => item.scheduleWeek === 'this');
  const nextWeek = allItems.filter((item) => item.scheduleWeek === 'next');

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-bold text-xl">AIKnow 라이브 클래스</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto py-8 md:py-12 px-4">
        <h1 className="text-xl md:text-3xl font-bold text-gray-900 md:mb-8 mb-4">
          전체 클래스
        </h1>

        {/* 모바일 탭 - md 이상에서 숨김 */}
        <div className="md:hidden border-t border-gray-100 flex mb-4">
          <button
            onClick={() => setActiveTab('this')}
            className={`flex-1 py-3 text-sm font-bold transition-colors ${
              activeTab === 'this'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-400'
            }`}
          >
            이번주 클래스
          </button>
          <button
            onClick={() => setActiveTab('next')}
            className={`flex-1 py-3 text-sm font-bold transition-colors ${
              activeTab === 'next'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-400'
            }`}
          >
            다음주 클래스
          </button>
        </div>

        {/* 데스크탑: 2컬럼 */}
        <div className="hidden md:grid grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-blue-600 mb-4">
              이번주 클래스
            </h2>
            {thisWeek.length > 0 ? (
              <div className="flex flex-col gap-3">
                {thisWeek.map((item) => (
                  <ClassCard key={item.id} {...item} />
                  ))}
              </div>
              ) : (
              <div className="bg-white border border-gray-100 rounded-2xl py-10 text-center text-gray-400">
                이번주 예정된 클래스가 없습니다.
              </div>
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold text-purple-600 mb-4">
              다음주 클래스
            </h2>

            {nextWeek.length > 0 ? (
              <div className="flex flex-col gap-3">
                {nextWeek.map((item) => (
                  <ClassCard key={item.id} {...item} />
                  ))}
              </div>
              ) : (
              <div className="bg-white border border-gray-100 rounded-2xl py-10 text-center text-gray-400">
                다음주 예정된 클래스가 없습니다.
              </div>
              )}
            </div>
          </div>

          {/* 모바일: 탭 전환 */}
          <div className="md:hidden">
            {activeTab === 'this' ? (
              thisWeek.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {thisWeek.map((item) => (
                    <ClassCard key={item.id} {...item} />
                    ))}
                </div>
                ) : (
                  <div className="bg-white border border-gray-100 rounded-2xl py-10 text-center text-gray-400">
                    이번주 예정된 클래스가 없습니다.
                  </div>
                )
              ) : nextWeek.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {nextWeek.map((item) => (
                    <ClassCard key={item.id} {...item} />
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-gray-100 rounded-2xl py-10 text-center text-gray-400">
                  다음주 예정된 클래스가 없습니다.
                </div>
              )
            }
          </div>

        {/* 모바일: 탭 전환 */}
        <div className="md:hidden flex flex-col gap-3">
          {(activeTab === 'this' ? thisWeek : nextWeek).map((item) => (
            <ClassCard key={item.id} {...item} />
          ))}
        </div>

        {hasNextPage && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-2 rounded-full text-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all cursor-pointer"
            >
              {isFetchingNextPage ? '로딩중...' : '더 보기'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassList;
